/** Initiatives — groups of projects, read as one.
 *
 * Ben Wood's request (2026-10-07), with Brian's rules: a project belongs to one initiative
 * at most; health is the worst of the live projects unless a Portfolio Manager overrides it
 * with a reason — and the computed colour stays on screen beside any override; Portfolio
 * Managers and Administrators create and manage them.
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, Layers3, Plus, X } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import {
  Button,
  DateInput,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  inputClass,
} from '@/components/ui'
import { formatDate, ragTone, statusLabel, statusTone } from '@/lib/domain'
import { usePeople } from '@/features/people/usePeople'
import { useProjects } from '@/features/projects/useProjects'
import {
  useCreateInitiative,
  useDeleteInitiative,
  useInitiative,
  useInitiatives,
  useSetMembership,
  useUpdateInitiative,
  type Health,
  type Initiative,
} from '@/features/initiatives/useInitiatives'

const HEALTH_LABEL: Record<Health, string> = { green: 'Green', amber: 'Amber', red: 'Red', none: 'No colour yet' }

function HealthBadge({ health }: { health: Health }) {
  return health === 'none' ? <Pill>{HEALTH_LABEL.none}</Pill> : <Pill tone={ragTone(health)}>{HEALTH_LABEL[health]}</Pill>
}

function Progress({ value }: { value: number }) {
  return (
    <div
      className="h-2 overflow-hidden rounded-pill"
      style={{ backgroundColor: 'color-mix(in srgb, var(--fg) 10%, transparent)' }}
      role="img"
      aria-label={`${value}% complete`}
    >
      <div className="h-full rounded-pill" style={{ width: `${value}%`, backgroundColor: 'var(--status-info)' }} />
    </div>
  )
}

function InitiativeCard({ initiative }: { initiative: Initiative }) {
  return (
    <Link to={`/initiatives/${initiative.id}`} className="card block p-4 transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-heading text-xl text-fg">{initiative.name}</h2>
          <p className="text-xs text-fg-muted">
            {initiative.ownerName ? `Owned by ${initiative.ownerName}` : 'No owner yet'}
            {initiative.targetDate ? ` · target ${formatDate(initiative.targetDate)}` : ''}
          </p>
        </div>
        <HealthBadge health={initiative.health} />
      </div>
      {initiative.healthOverride ? (
        <p className="mt-1 text-[11px] text-fg-muted">
          Set by hand — the projects alone say {HEALTH_LABEL[initiative.computedHealth].toLowerCase()}.
        </p>
      ) : null}
      <div className="mt-3 flex items-baseline justify-between text-sm">
        <span className="text-fg-muted">
          {initiative.projectCount} project{initiative.projectCount === 1 ? '' : 's'} · {initiative.liveCount} live
          {initiative.atRiskCount ? ` · ${initiative.atRiskCount} at risk` : ''}
        </span>
        <span className="font-heading text-lg text-fg">{initiative.percentComplete}%</span>
      </div>
      <div className="mt-1.5">
        <Progress value={initiative.percentComplete} />
      </div>
    </Link>
  )
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const create = useCreateInitiative()
  const { data: people } = usePeople()
  const [form, setForm] = useState({ name: '', description: '', owner_user_id: '', target_date: '' })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-canvas p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="New initiative">
        <h2 className="mb-4 font-heading text-2xl text-fg">New initiative</h2>
        {create.error ? <div className="mb-3"><ErrorState error={create.error} /></div> : null}
        <div className="space-y-3">
          <Field label="Name">
            <input className={inputClass} value={form.name} autoFocus onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="What it is for">
            <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Owner">
              <SearchSelect
                value={form.owner_user_id}
                onChange={(v) => setForm((f) => ({ ...f, owner_user_id: v }))}
                options={(people ?? []).map((p) => ({ value: p.id, label: p.displayName }))}
                emptyLabel="Nobody yet"
              />
            </Field>
            <Field label="Target date">
              <DateInput value={form.target_date} onChange={(v) => setForm((f) => ({ ...f, target_date: v }))} />
            </Field>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: form.name.trim(),
                  description: form.description.trim(),
                  owner_user_id: form.owner_user_id || null,
                  target_date: form.target_date || null,
                },
                { onSuccess: (created) => onCreated(created.id) },
              )
            }
          >
            Create initiative
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Override the computed colour, or go back to it. A reason is required and shown. */
function HealthControl({ initiative }: { initiative: Initiative }) {
  const update = useUpdateInitiative()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<Health>(initiative.healthOverride ?? 'green')
  const [reason, setReason] = useState('')

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="micro-label text-fg-muted">Health</span>
        <HealthBadge health={initiative.health} />
        <span className="text-xs text-fg-muted">
          {initiative.healthOverride
            ? `set by hand — the projects alone say ${HEALTH_LABEL[initiative.computedHealth].toLowerCase()}`
            : 'the worst colour among its live projects'}
        </span>
        <span className="flex-1" />
        {initiative.canManage && !editing ? (
          <>
            <Button onClick={() => setEditing(true)}>Override</Button>
            {initiative.healthOverride ? (
              <Button
                onClick={() =>
                  update.mutate({ id: initiative.id, health_override: null, row_version: initiative.rowVersion })
                }
                disabled={update.isPending}
              >
                Use computed
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {initiative.healthOverride ? (
        <p className="mt-2 text-sm text-fg">
          “{initiative.healthOverrideReason}”
          <span className="block text-[11px] text-fg-muted">
            {initiative.healthOverrideBy}
            {initiative.healthOverrideAt ? ` · ${new Date(initiative.healthOverrideAt).toLocaleDateString()}` : ''}
          </span>
        </p>
      ) : null}
      {editing ? (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            {(['green', 'amber', 'red'] as const).map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setValue(h)}
                aria-pressed={value === h}
                className="rounded-pill border px-3 py-1 text-sm font-semibold"
                style={value === h ? { borderColor: `var(--status-${h})`, color: `var(--status-${h})` } : { borderColor: 'var(--border)' }}
              >
                {HEALTH_LABEL[h]}
              </button>
            ))}
          </div>
          <input
            className={inputClass}
            placeholder="Why — shown beside the computed colour"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {update.error ? <ErrorState error={update.error} /> : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={reason.trim().length < 3 || update.isPending}
              onClick={() =>
                update.mutate(
                  { id: initiative.id, health_override: value, health_override_reason: reason.trim(), row_version: initiative.rowVersion },
                  { onSuccess: () => { setEditing(false); setReason('') } },
                )
              }
            >
              Save override
            </Button>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Membership({ initiative, others }: { initiative: Initiative; others: Map<string, string> }) {
  const membership = useSetMembership()
  const { data: projectData } = useProjects({})
  const [adding, setAdding] = useState('')
  const inThis = new Set(initiative.projects.map((p) => p.id))
  const candidates = (projectData?.projects ?? []).filter((p) => !inThis.has(p.id))

  const add = (projectId: string) => {
    const elsewhere = others.get(projectId)
    if (elsewhere && !window.confirm(`This project is in “${elsewhere}”. Move it here? A project belongs to one initiative.`)) return
    membership.mutate({ initiativeId: initiative.id, projectId, add: true, move: Boolean(elsewhere) }, { onSuccess: () => setAdding('') })
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-[260px] flex-1">
        <Field label="Add a project">
          <SearchSelect
            value={adding}
            onChange={(v) => v && add(v)}
            options={candidates.map((p) => ({
              value: p.id,
              label: p.name,
              hint: others.get(p.id) ? `in ${others.get(p.id)}` : p.teamShortLabel,
            }))}
            emptyLabel="Choose a project…"
          />
        </Field>
      </div>
      {membership.error ? <ErrorState error={membership.error} /> : null}
    </div>
  )
}

function Detail({ id, others, onClose }: { id: string; others: Map<string, string>; onClose: () => void }) {
  const { data: initiative, isPending, error } = useInitiative(id)
  const membership = useSetMembership()
  const remove = useDeleteInitiative()

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <aside className="h-full w-full max-w-[760px] overflow-auto bg-canvas p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Initiative">
        <div className="mb-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="micro-label text-fg-muted">Initiative</p>
            <h2 className="font-heading text-2xl text-fg">{initiative?.name ?? '…'}</h2>
            {initiative ? (
              <p className="text-sm text-fg-muted">
                {initiative.ownerName ? `Owned by ${initiative.ownerName}` : 'No owner yet'}
                {initiative.targetDate ? ` · target ${formatDate(initiative.targetDate)}` : ''}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-fg-muted hover:bg-surface" aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>

        {isPending ? (
          <Loading />
        ) : !initiative ? (
          <ErrorState error={error} />
        ) : (
          <div className="space-y-5">
            {initiative.description ? <p className="text-sm text-fg-muted">{initiative.description}</p> : null}

            <HealthControl initiative={initiative} />

            <div className="card p-4">
              <div className="flex items-baseline justify-between">
                <span className="micro-label text-fg-muted">Progress — average of its projects</span>
                <span className="font-heading text-2xl text-fg">{initiative.percentComplete}%</span>
              </div>
              <div className="mt-2"><Progress value={initiative.percentComplete} /></div>
              <p className="mt-2 text-xs text-fg-muted">
                {initiative.projectCount} projects · {initiative.liveCount} live · {initiative.completeCount} complete
                {initiative.atRiskCount ? ` · ${initiative.atRiskCount} amber or red` : ''}
                {initiative.overdueCount ? ` · ${initiative.overdueCount} past target` : ''}
              </p>
              {initiative.projectsRunPastTarget ? (
                <p className="mt-2 flex items-center gap-1.5 text-xs" style={{ color: 'var(--status-amber)' }}>
                  <AlertTriangle size={13} aria-hidden />
                  Its projects run to {formatDate(initiative.latestProjectTarget)}, past the initiative&apos;s own target.
                </p>
              ) : null}
            </div>

            <section>
              <p className="micro-label mb-2 text-fg-muted">Projects</p>
              {initiative.projects.length ? (
                <ul className="card divide-y divide-border">
                  {initiative.projects.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
                      <Link to={`/projects/${p.id}`} className="min-w-0 flex-1 truncate font-medium text-fg hover:underline">{p.name}</Link>
                      <Pill tone={statusTone(p.status)}>{statusLabel(p.status)}</Pill>
                      {p.ragStatus !== 'none' ? <Pill tone={ragTone(p.ragStatus)}>{p.ragStatus}</Pill> : null}
                      <span className="w-12 text-right text-sm text-fg">{p.percentComplete}%</span>
                      <span className="w-24 text-right text-xs text-fg-muted">{formatDate(p.targetDate)}</span>
                      {initiative.canManage ? (
                        <button
                          type="button"
                          className="text-fg-muted hover:text-fg"
                          aria-label={`Take ${p.name} out of this initiative`}
                          title="Take it out of this initiative — the project itself is unchanged"
                          onClick={() => membership.mutate({ initiativeId: initiative.id, projectId: p.id, add: false })}
                        >
                          <X size={14} aria-hidden />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-fg-muted">No projects yet.</p>
              )}
              {initiative.canManage ? <div className="mt-3"><Membership initiative={initiative} others={others} /></div> : null}
            </section>

            {initiative.nextMilestones?.length ? (
              <section>
                <p className="micro-label mb-2 text-fg-muted">Next milestones across the projects</p>
                <ul className="card divide-y divide-border text-sm">
                  {initiative.nextMilestones.map((m, i) => (
                    <li key={`${m.title}-${i}`} className="flex flex-wrap items-center gap-2 px-4 py-2">
                      <span className="min-w-0 flex-1 text-fg">{m.title}<span className="text-xs text-fg-muted"> · {m.projectName}</span></span>
                      <span className="text-xs" style={{ color: m.isOverdue ? 'var(--status-red)' : 'var(--fg-muted)' }}>
                        {m.dueDate ? formatDate(m.dueDate) : 'No date'}{m.isOverdue ? ' · overdue' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {initiative.waitingOn?.length ? (
              <section>
                <p className="micro-label mb-2 text-fg-muted">Waiting on</p>
                <ul className="card divide-y divide-border text-sm">
                  {initiative.waitingOn.map((w, i) => (
                    <li key={i} className="px-4 py-2 text-fg">
                      <b>{w.projectName}</b> waits on <b>{w.waitsOn}</b>
                      <span className="text-xs text-fg-muted"> · {statusLabel(w.waitsOnStatus)}{w.insideInitiative ? '' : ' · outside this initiative'}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {initiative.canManage ? (
              <div className="border-t border-border pt-4">
                <Button
                  variant="destructive"
                  onClick={() => {
                    const reason = window.prompt('Remove this initiative? Its projects stay exactly as they are. Why?')
                    if (reason && reason.trim().length >= 3) remove.mutate({ id: initiative.id, reason: reason.trim() }, { onSuccess: onClose })
                  }}
                >
                  Remove initiative
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </aside>
    </div>
  )
}

export function InitiativesPage() {
  const { data, isPending, error } = useInitiatives()
  const { initiativeId } = useParams<{ initiativeId: string }>()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const initiatives = data?.initiatives ?? []
  // project id -> the initiative it is in, to warn before moving one between initiatives.
  const memberOf = new Map<string, string>()
  for (const i of initiatives) for (const p of i.projects) memberOf.set(p.id, i.name)
  const othersFor = (id: string) =>
    new Map([...memberOf].filter(([, name]) => name !== initiatives.find((i) => i.id === id)?.name))

  return (
    <div className="mx-auto max-w-[1400px] pt-2">
      <header className="mb-6 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 font-heading text-3xl text-fg">
            <Layers3 size={26} aria-hidden style={{ color: 'var(--brand)' }} />
            Initiatives
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-muted">
            Groups of projects read as one: combined progress, the worst health among the live
            projects (or a colour set by hand, with the reason), the next milestones across all
            of them, and what the group waits on.
          </p>
        </div>
        {data?.canManage ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={14} aria-hidden />
            New initiative
          </Button>
        ) : null}
      </header>

      {isPending ? (
        <Loading label="Loading initiatives" />
      ) : error ? (
        <ErrorState error={error} />
      ) : initiatives.length === 0 ? (
        <EmptyState title="No initiatives yet">
          An initiative groups related projects — a programme, a migration in several parts —
          and reports on them together.{' '}
          {data?.canManage ? 'Press New initiative to start one.' : 'A Portfolio Manager or an Administrator sets them up.'}
        </EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {initiatives.map((i) => (
            <InitiativeCard key={i.id} initiative={i} />
          ))}
        </div>
      )}

      {initiativeId ? (
        <Detail id={initiativeId} others={othersFor(initiativeId)} onClose={() => navigate('/initiatives')} />
      ) : null}
      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            navigate(`/initiatives/${id}`)
          }}
        />
      ) : null}
    </div>
  )
}
